import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from 'react-simple-maps';
import {Motion, spring} from 'react-motion';

import './styles.scss';

import geoJsonEurope from '../../maps/europe.json';

import {geoPath, geoMercator, GeoProjection} from 'd3-geo';
import {Category, DataPoint, View} from '../../App';
import {feature} from 'topojson-client';
import Icon from '../Icon/Icon';
import {displayInvestment, getMarkerSize} from '../../utils';

export interface Marker {
  id: string;
  city?: string;
  country?: string;
  coords: [number, number];
  entity?: string;
  category?: Category;
  children?: Marker[];
  investorCountry?: string;
  investedSum?: number;
}

const PROJECTION_ROTATE_EU: [number, number, number] = [-20.0, -80.0, 0];

// Center of Europe map
const INIT_CENTER_EU = [18, 50.5];

const SCALE = 900;

const SingleMarker = ({
  marker,
  w,
  setTooltipContent,
  transform,
  visible = true,
  content,
  onClick,
}: {
  marker: Marker;
  w: number;
  setTooltipContent: (tooltip: React.ReactNode) => void;
  transform?: string;
  visible?: boolean;
  content?: React.ReactNode;
  onClick?: (marker: Marker) => void;
}) => {
  if (!visible) return null;
  const markerSize = getMarkerSize(marker);
  const iconSize = markerSize / w;

  const tooltipContent = useMemo(() => {
    return (
      <>
        <div>
          <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-map-pin"
            >
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {marker.city}
          </div>
          <div style={{paddingTop: '10px'}}>
            {marker.entity ? `${marker.entity}, ` : ''}
          </div>
          <div style={{fontSize: '1rem', paddingTop: '2px'}}>
            {displayInvestment(marker)}
          </div>
        </div>
      </>
    );
  }, [marker]);

  return (
    <g
      key={marker.id}
      className={`map-marker marker-appear`}
      transform={transform}
      onClick={() => {
        onClick?.(marker);
        setTooltipContent(null);
      }}
      onMouseOver={() => {
        setTooltipContent(tooltipContent);
      }}
      onMouseEnter={() => {
        setTooltipContent(tooltipContent);
      }}
      onMouseLeave={() => {
        setTooltipContent(null);
      }}
    >
      <circle
        className={`circle-inner ${marker.investorCountry?.toLowerCase()}`}
        r={iconSize}
      />
      {typeof content === 'string' ? (
        <text
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            transform: `scale(${markerSize > 15 ? 2 : 1})`,
          }}
        >
          {content}
        </text>
      ) : (
        <g transform={`scale(${iconSize / 6})`}>{content}</g>
      )}
    </g>
  );
};

const MultipleMarkers = ({
  marker,
  count,
  level,
  visibleLevel,
  w,
  offsetX = 0,
  offsetY = 0,
  setTooltipContent,
  onClick,
}: {
  marker: Marker;
  count: number;
  level: number;
  visibleLevel: number;
  w: number;
  offsetX?: number;
  offsetY?: number;
  setTooltipContent: (tooltip: React.ReactNode) => void;
  onClick?: (marker: Marker) => void;
}) => {
  return (
    <g>
      {marker.children.map((child, i) => {
        const distance = (count * 7) / w;
        const angle = (2 * Math.PI) / count;
        const x = Math.cos(angle * i) * distance + offsetX;
        const y = Math.sin(angle * i) * distance + offsetY;

        if (visibleLevel === level) {
          return (
            <SingleMarker
              key={i}
              marker={child}
              w={w}
              setTooltipContent={setTooltipContent}
              onClick={onClick}
              transform={`translate(${x}, ${y})`}
              content={
                child.children.length > 1 ? (
                  child.children.length.toString()
                ) : (
                  <Icon
                    type={child.category}
                    width={10}
                    height={10}
                    x={-5}
                    y={-5}
                    fill="white"
                  />
                )
              }
            />
          );
        }
        if (visibleLevel === level + 1) {
          return (
            <MultipleMarkers
              key={i}
              marker={child}
              count={child.children.length}
              level={level + 1}
              visibleLevel={visibleLevel}
              w={w}
              offsetX={x}
              offsetY={y}
              setTooltipContent={setTooltipContent}
              onClick={onClick}
            />
          );
        }
      })}
    </g>
  );
};

interface MapChartProps {
  countries: string[];
  filteredCity: string | null;
  hoveredCity: string | null;
  hasDrawer?: boolean;
  setCountry: (country: string | null) => void;
  setFilteredCity: (city: string | null) => void;
  data: DataPoint[];
  setTooltipContent: (tooltip: React.ReactNode) => void;
  availableCountries: Map<string, boolean>;
  setId?: (id: string) => void;
}

const MapChart = ({
  countries,
  filteredCity,
  hoveredCity,
  hasDrawer,
  setCountry,
  setFilteredCity,
  data,
  setTooltipContent,
  availableCountries,
  setId,
}: MapChartProps) => {
  const DEFAULT_ZOOM = window.innerWidth < 767 ? 6 : 3;
  // Mobile markers appear too small
  const markerSizeMultiplier = window.innerWidth < 767 ? 0.5 : 1;
  const [animated, setAnimated] = useState(false);
  const [hoveredCountry, setHoveredCountry] = useState('');
  const [scaleFactor, setScaleFactor] = useState<number>(DEFAULT_ZOOM);
  const [zoomableProps, setZoomableProps] = useState({
    zoom: DEFAULT_ZOOM,
    center: INIT_CENTER_EU,
  });
  const [isPanningDisabled, setIsPanningDisabled] = useState(false);

  const transformedMarkers = useMemo(() => {
    const groupedMarkers: { [key: string]: Marker[] } = {};
    const markers: Marker[] = [];

    data.forEach((dataPoint) => {
      // eslint-disable-next-line max-len
      const groupKey = dataPoint.coords.join(',');

      if (!groupedMarkers[groupKey]) {
        groupedMarkers[groupKey] = [];
      }

      groupedMarkers[groupKey].push({
        id: dataPoint.id,
        entity: dataPoint.Entity,
        category: dataPoint.Category,
        coords: dataPoint.coords,
        country: dataPoint.Country,
        city: dataPoint.Location,
        investorCountry: dataPoint.Investor_country,
        investedSum: Number(dataPoint.Amount_EUR_mln),
        children: [],
      });
    });

    Object.keys(groupedMarkers).forEach((groupKey) => {
      let marker: Marker = null;
      if (groupedMarkers[groupKey].length === 1) {
        // Single location marker
        marker = groupedMarkers[groupKey][0];
      } else {
        // Separate into groups by investor country
        const markerGroupsByInvestorCountry: { [key: string]: Marker[] } = {};
        groupedMarkers[groupKey].forEach((groupMarker) => {
          if (!markerGroupsByInvestorCountry[groupMarker.investorCountry]) {
            markerGroupsByInvestorCountry[groupMarker.investorCountry] = [];
          }
          markerGroupsByInvestorCountry[groupMarker.investorCountry].push(
              groupMarker,
          );
        });

        // Multiple groups in one location
        marker = {
          id: groupedMarkers[groupKey][0].id,
          coords: groupedMarkers[groupKey][0].coords,
          city: groupedMarkers[groupKey][0].city,
          investedSum: groupedMarkers[groupKey].reduce(
              (sum, marker) => sum + marker.investedSum,
              0,
          ),
          investorCountry: 'mix',
          children: [],
        };

        Object.keys(markerGroupsByInvestorCountry).forEach(
            (investorCountry) => {
            // Markers for each investor country
              const invCountryGroupMarker = {
                id: markerGroupsByInvestorCountry[investorCountry][0].id,
                coords: groupedMarkers[groupKey][0].coords,
                city: groupedMarkers[groupKey][0].city,
                country: groupedMarkers[groupKey][0].country,
                category:
                markerGroupsByInvestorCountry[investorCountry][0].category,
                investedSum: markerGroupsByInvestorCountry[
                    investorCountry
                ].reduce((sum, marker) => sum + marker.investedSum, 0),
                investorCountry,
                children: markerGroupsByInvestorCountry[investorCountry],
              };

              marker.children.push(invCountryGroupMarker);
            },
        );
      }

      markers.push(marker);
    });

    // Move filtered city to the end of the list
    // to make it display last (because z-index doesn't work with svg)
    const focusedCity = hoveredCity;
    if (focusedCity) {
      const filteredCityIndex = markers.findIndex(
          (marker) => marker.city === focusedCity,
      );
      const filteredCityMarker = markers.splice(filteredCityIndex, 1);
      markers.push(filteredCityMarker[0]);
    }
    return markers;
  }, [data, hoveredCity]);

  const isAvailable = useCallback(
      (country: string) => {
        return availableCountries.get(country);
      },
      [availableCountries],
  );

  useEffect(() => {
    // Trigger any animation logic you have
    setAnimated(true);

    // Reset to defaults first (optional)
    setZoomableProps({
      center: INIT_CENTER_EU,
      zoom: DEFAULT_ZOOM,
    });
    setScaleFactor(DEFAULT_ZOOM);

    const defaultCountries = [
      'Slovakia',
      'Czech Republic',
      'Hungary',
      'Poland',
    ];

    // Decide which countries to show (fallback to Slovakia if none selected)
    const countriesToShow = countries.length > 0 ? countries : defaultCountries;

    // Filter the TopoJSON geometries for the chosen countries
    const matchedGeometries = geoJsonEurope.objects.europe.geometries.filter(
        (g: any) => countriesToShow.includes(g.properties.NAME),
    );

    // Combine into one geometry collection
    const multiFeature = feature(geoJsonEurope as any, {
      type: 'GeometryCollection',
      geometries: matchedGeometries as any,
    }) as GeoJSON.FeatureCollection;

    // Create a path generator
    const path = geoPath().projection(projection());

    // Compute the bounding box of all selected features
    const bounds = path.bounds(multiFeature);
    const dx = bounds[1][0] - bounds[0][0];
    const dy = bounds[1][1] - bounds[0][1];

    // Some formula to define 'size' from dx, dy
    const size = (dx + dy) / 3.5;
    // If you want to keep a minimum or maximum zoom
    const newZoom =
      matchedGeometries.length > 0 ? Math.max(200 / size, 3) : DEFAULT_ZOOM;

    // Center: get the midpoint of the bounding box in projected coordinates
    const [minX, minY] = bounds[0];
    const [maxX, maxY] = bounds[1];
    const centerProjected: [number, number] = [
      (minX + maxX) / 2,
      (minY + maxY) / 2,
    ];

    // Convert from projected coords back to longitude/latitude
    const newCenter = projection().invert(centerProjected);

    // Use setTimeout(0) to ensure the re-render can occur after the first draw
    setTimeout(() => {
      setZoomableProps((prev) => ({
        ...prev,
        zoom: newZoom,
        center: newCenter,
      }));
      setScaleFactor(newZoom);
    }, 0);
  }, [countries]);

  useEffect(() => {
    const markerEl = document.querySelector(`[data-city="${filteredCity}"`);
    if (!markerEl) return;
    const viewportOffset = markerEl.getBoundingClientRect();
    // these are relative to the viewport, i.e. the window
    const markerLeftOffset = viewportOffset.left;

    // Only center on markers that would be covered by Drawer
    const isMarkerInRightViewHalf = markerLeftOffset > window.innerWidth / 2;
    if (filteredCity) {
      setAnimated(true);
      const marker = transformedMarkers.find(
          (marker) => marker.city === filteredCity,
      );
      setZoomableProps({
        ...zoomableProps,
        center: [
          isMarkerInRightViewHalf ?
            Number(marker.coords[0]) :
            zoomableProps.center[0],
          Number(marker.coords[1]),
        ],
      });
    }
  }, [filteredCity]);

  useEffect(() => {
    setZoomableProps({
      center: INIT_CENTER_EU,
      zoom: DEFAULT_ZOOM,
    });
    setAnimated(false);
    setScaleFactor(DEFAULT_ZOOM);
  }, []);

  const projection = (): GeoProjection => {
    return geoMercator().rotate(PROJECTION_ROTATE_EU).scale(SCALE);
  };

  const handleClick = (geo: any) => {
    if (!isAvailable(geo.properties.NAME)) return;
    setCountry(geo.properties.NAME);
    // Zoom in on the country
  };

  const handleHover = (geo: any) => {
    setHoveredCountry(geo.properties.NAME);
  };

  const zoomOnCity = (city: string) => {
    setAnimated(true);
    setZoomableProps({
      ...zoomableProps,
      center: transformedMarkers.find((marker) => marker.city === city).coords,
      zoom: 11,
    });
    setScaleFactor(11);
  };

  const clickMarkerHandler = (marker: Marker) => {
    // If it's multiple, open city view
    if (marker.children.length > 1) {
      zoomOnCity(marker.city);
      return;
    }
    setFilteredCity(marker?.city === filteredCity ? null : marker?.city);
    setId?.(marker?.id);
  };

  const getVisibleLevel = useCallback((w: number) => {
    if (w > 10) return 1;
    return 0;
  }, []);

  return (
    <div data-tip data-for="marker-tooltip">
      <Motion
        defaultStyle={{
          z: zoomableProps.zoom,
          x: zoomableProps.center[0],
          y: zoomableProps.center[1],
          w: scaleFactor,
        }}
        style={{
          z: animated ? spring(zoomableProps.zoom) : zoomableProps.zoom,
          x: animated ?
            spring(zoomableProps.center[0]) :
            zoomableProps.center[0],
          y: animated ?
            spring(zoomableProps.center[1]) :
            zoomableProps.center[1],
          w: animated ? spring(scaleFactor) : scaleFactor,
        }}
        onRest={() => setAnimated(false)}
      >
        {({z, x, y, w}) => (
          <ComposableMap
            projection="geoMercator"
            className={`MapChart ${hasDrawer ? 'has-drawer' : ''}`}
            projectionConfig={{
              rotate: PROJECTION_ROTATE_EU,
              scale: SCALE,
            }}
          >
            <ZoomableGroup
              zoom={z}
              center={[x, y]}
              maxZoom={40}
              minZoom={2.8}
              onMove={({k}) => setScaleFactor(k)}
              filterZoomEvent={() => !isPanningDisabled}
              onMoveEnd={(e) => {
                setZoomableProps({
                  zoom: e.zoom,
                  center: e.coordinates,
                });
              }}
            >
              <Geographies geography={geoJsonEurope}>
                {({geographies}) =>
                  geographies.map((geo: any) => {
                    return (
                      <Geography
                        onClick={() => handleClick(geo)}
                        key={geo.rsmKey}
                        geography={geo}
                        fill={
                          isAvailable(geo.properties.NAME) ?
                            hoveredCountry === geo.properties.NAME ?
                              '#aac0d3' :
                              '#b4c8da' :
                            '#c7d8e3'
                        }
                        stroke="#fff"
                        strokeWidth={0.15}
                        onMouseOver={() => handleHover(geo)}
                        onMouseOut={() => setHoveredCountry('')}
                        style={{
                          default: {outline: 'none', cursor: 'pointer'},
                          hover: {outline: 'none', cursor: 'pointer'},
                          pressed: {outline: 'none'},
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {transformedMarkers.map((marker, i) => {
                return (
                  <Marker
                    key={marker.id}
                    coordinates={marker?.coords}
                    style={{
                      default: {
                        fontSize: `${10 / w / markerSizeMultiplier}px`,
                      },
                      hover: {
                        fontSize: `${10 / w / markerSizeMultiplier}px`,
                      },
                      pressed: {
                        fontSize: `${10 / w / markerSizeMultiplier}px`,
                      },
                    }}
                  >
                    <g data-city={marker?.city}>
                      {marker.children.length > 0 ? (
                        <MultipleMarkers
                          marker={marker}
                          count={marker.children.length}
                          level={0}
                          visibleLevel={getVisibleLevel(w)}
                          setTooltipContent={setTooltipContent}
                          onClick={clickMarkerHandler}
                          w={w * markerSizeMultiplier}
                        />
                      ) : (
                        <SingleMarker
                          marker={marker}
                          w={w * markerSizeMultiplier}
                          setTooltipContent={setTooltipContent}
                          onClick={clickMarkerHandler}
                          content={
                            <Icon
                              type={marker.category}
                              width={10}
                              height={10}
                              x={-5}
                              y={-5}
                              fill="white"
                            />
                          }
                        />
                      )}
                    </g>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>
        )}
      </Motion>
    </div>
  );
};

export default MapChart;
